/**
 * CANONICALISATION AND CHECKSUM — the client half of a mirrored pair.
 *
 * This file is a BYTE-FOR-BYTE mirror of
 * `backend/src/game/config/config.codec.ts` below this header. It has to be:
 * the whole point of `checksum` is that the browser recomputes it over the
 * document it received and compares it with the number the server computed over
 * the document it sent. Two implementations that disagree about key order, or
 * about how a number is written, produce a checksum mismatch on every fetch and
 * a client that can never refresh its card table.
 *
 * `frontend/src/engine/config/config.test.ts` pins the outputs — the canonical
 * string for a hand-written object, and the checksum of a fixed input — so a
 * divergence is a failing assertion here rather than a stuck client in the wild.
 *
 * It is duplicated rather than shared because the only shared package between
 * these two trees is `@rga/sim`, and the sim imports nothing and may not learn
 * about config (ADR-0003). A ~150-line pure function under a parity test is the
 * cheaper of the two mistakes.
 *
 * Everything below this comment is identical to the server's copy.
 */
/** 32-bit FNV-1a offset basis and prime (the standard parameters). */
const FNV_OFFSET = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * FNV-1a over the UTF-16 code units of `text`, low byte first. Returns an
 * unsigned 32-bit integer.
 */
export function fnv1a32(text: string): number {
  let h = FNV_OFFSET;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h = Math.imul(h ^ (c & 0xff), FNV_PRIME);
    h = Math.imul(h ^ ((c >>> 8) & 0xff), FNV_PRIME);
  }
  return h >>> 0;
}

/** `fnv1a32` as exactly 8 lowercase hex characters. The wire form. */
export function checksumOf(text: string): string {
  return fnv1a32(text).toString(16).padStart(8, '0');
}

/**
 * Deterministic JSON: object keys in ascending code-unit order, arrays in the
 * order given, no whitespace.
 *
 * REFUSALS, all of them deliberate. Every one of these is a value that would
 * either serialise differently on two machines or silently vanish:
 *   - `undefined` and functions      — `JSON.stringify` drops them from objects
 *                                      and turns them into `null` in arrays.
 *   - non-integer / non-finite numbers — ADR-0003 forbids floats anywhere near
 *                                      the sim, and `0.1 + 0.2` does not
 *                                      round-trip. A float in a card payload is
 *                                      a defect, so this is the earliest place
 *                                      to catch it.
 *   - `-0`                            — serialises as `0` and compares equal to
 *                                      it, so it is normalised rather than
 *                                      allowed to hide.
 */
export function canonicalJson(value: unknown): string {
  const out: string[] = [];
  writeCanonical(value, out, 0);
  return out.join('');
}

/** Deepest structure a config document may nest. Guards against a cycle. */
const MAX_DEPTH = 12;

function writeCanonical(value: unknown, out: string[], depth: number): void {
  if (depth > MAX_DEPTH) throw new CanonicalJsonError('nested deeper than 12 levels');

  if (value === null) {
    out.push('null');
    return;
  }
  const kind = typeof value;
  if (kind === 'string') {
    out.push(JSON.stringify(value));
    return;
  }
  if (kind === 'boolean') {
    out.push(value === true ? 'true' : 'false');
    return;
  }
  if (kind === 'number') {
    const n = value as number;
    if (!Number.isFinite(n)) throw new CanonicalJsonError(`not a finite number: ${String(n)}`);
    if (!Number.isInteger(n)) throw new CanonicalJsonError(`not an integer: ${String(n)}`);
    out.push(String(n === 0 ? 0 : n));
    return;
  }
  if (Array.isArray(value)) {
    out.push('[');
    for (let i = 0; i < value.length; i++) {
      if (i > 0) out.push(',');
      writeCanonical(value[i], out, depth + 1);
    }
    out.push(']');
    return;
  }
  if (kind === 'object') {
    const record = value as Record<string, unknown>;
    const keys = Object.keys(record).sort(compareCodeUnits);
    out.push('{');
    let first = true;
    for (const key of keys) {
      const v = record[key];
      // An explicit `undefined` property is dropped by JSON.stringify, so
      // allowing it here would make two different objects canonicalise the
      // same. Optional card fields (`radius`, `lifetime`) are ABSENT, not
      // undefined, once a payload has been through JSON.
      if (v === undefined) continue;
      if (!first) out.push(',');
      first = false;
      out.push(JSON.stringify(key), ':');
      writeCanonical(v, out, depth + 1);
    }
    out.push('}');
    return;
  }
  throw new CanonicalJsonError(`cannot canonicalise a ${kind}`);
}

/** Ascending UTF-16 code-unit order — the same order on every engine. */
function compareCodeUnits(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export class CanonicalJsonError extends Error {
  constructor(message: string) {
    super(`canonical JSON: ${message}`);
    this.name = 'CanonicalJsonError';
  }
}

/**
 * THE BUCKET FUNCTION — the whole percentage-rollout model, in one line.
 *
 * `bucket(key, subjectId)` is in `[0, 100)`, stable forever, and independent
 * per flag because the key is inside the hash. An empty `subjectId` (an
 * unauthenticated visitor, a server-side global check) always lands in the same
 * bucket for a given key, which is why a flag meant to be global is written as
 * `rollout: 100` rather than left to chance at 0%.
 */
export function flagBucket(key: string, subjectId: string): number {
  return fnv1a32(`${key}:${subjectId}`) % 100;
}
